<template>
  <div class="digitalization-project-docu-responsibles">
    <title-panel
      :button-label="
        t('digitalizationProjectDocuResponsibles.addDocuResponsible')
      "
      :count="docuResponsibles?.length ?? 0"
      :display-button="displayAddDocuResponsibleButton()"
      :on-button-click="onAddDocuResponsibleButtonClick"
      :title="t('digitalizationProjectDocuResponsibles.docuResponsible', 2)"
    ></title-panel>

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      action-icon="delete"
      :action-icon-disabled="isLastDocuResponsible"
      :action-icon-tooltip-text="deleteIconTooltipText"
      :display-action-icon="isUserRole([Role.Admin])"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="docuResponsibles"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      @action-icon-click="deleteDocuResponsibleFromProject"
    >
    </table-wrapper>

    <ad-group-modal
      ref="modalRef"
      modal-id="docu-responsibles-modal"
      :group="AdGroup.DOCU_RESPONSIBLE"
      :title="t('digitalizationProjectDocuResponsibles.addDocuResponsible')"
      @selected-user="handleAddDocuResponsible"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import axios, { AxiosError } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { IDocuResponsible, Role } from 'types';
  import SpGroupModal from '../SpGroupModal/SpGroupModal.vue';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import { useRoles } from '@/composables/useRoles';
  import AdGroupModal from '../AdGroupModal/AdGroupModal.vue';
  import { AdGroup } from 'types';

  const props = defineProps<{ digitalizationProjectId: string }>();

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast, createWarningToast } =
    useToast();

  const docuResponsibles = ref<IDocuResponsible[]>([]);
  const { pageOptions, filters, isLoading } = useTableWrapper<IDocuResponsible>(
    TableMode.CLIENT_SIDE,
    'lastName',
    getDocuResponsibles,
  );

  const filterConfiguration = new TableFiltersBuilder<IDocuResponsible>({
    translationKey: 'digitalizationProjectDocuResponsibles',
  })
    .addInputFilter('firstName', { width: '208px' })
    .addInputFilter('lastName', { width: '208px' })
    .addInputFilter('email', { width: '208px' })
    .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<IDocuResponsible>({
      sortable: true,
      labelTranslationKey: 'digitalizationProjectDocuResponsibles',
    })
      .addField('firstName', { width: '36%' })
      .addField('lastName', { width: '36%' })
      .addField('email', { width: '34%' })
      .build(),
  );

  async function getDocuResponsibles() {
    try {
      const response = await axios.get(
        endpointsList.digitalizationProjects.getDigitalizationProjectDocuResponsibles(
          props.digitalizationProjectId,
        ),
      );
      docuResponsibles.value = response.data;

      const isUnkownUser = docuResponsibles.value.some(
        (docuResponsible) => docuResponsible.firstName === 'unknown',
      );
      if (isUnkownUser) {
        createWarningToast(
          t('digitalizationProjectDocuResponsibles.warnings.unknownUser'),
        );
      }
    } catch {
      createErrorToast(
        t(
          'digitalizationProjectDocuResponsibles.errors.errorGettingDocuResponsbiles.long',
        ),
      );
    }
  }

  async function handleAddDocuResponsible(docuResponsible: IDocuResponsible) {
    try {
      await axios.patch(
        endpointsList.digitalizationProjects.addDocuResponsibleToDigitalizationProject(
          props.digitalizationProjectId,
          docuResponsible.oid,
        ),
      );
      getDocuResponsibles();
      createSuccessToast(
        t('digitalizationProjectDocuResponsibles.docuResponsibleAdded'),
      );
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response?.status === 409) {
        createErrorToast(
          t(
            'digitalizationProjectDocuResponsibles.errors.docuResponsibleAlreadyAdded.long',
          ),
        );
        return;
      }
      createErrorToast(
        t(
          'digitalizationProjectDocuResponsibles.errors.errorAddingDocuResponsible.long',
        ),
      );
    }
  }

  async function deleteDocuResponsibleFromProject(
    docuResponsible: IDocuResponsible,
  ) {
    try {
      isLoading.value = true;
      await axios.delete(
        endpointsList.digitalizationProjects.removeDocuResponsibleFromDigitalizationProject(
          props.digitalizationProjectId,
          docuResponsible.oid,
        ),
      );
      createSuccessToast(
        t('digitalizationProjectDocuResponsibles.docuResponsibleRemoved'),
      );
      getDocuResponsibles();
    } catch (err) {
      createErrorToast(
        t(
          'digitalizationProjectDocuResponsibles.errors.errorRemovingDocuResponsible',
        ),
      );
    } finally {
      isLoading.value = false;
    }
  }

  const isLastDocuResponsible = computed(
    () => docuResponsibles.value.length <= 1,
  );

  const deleteIconTooltipText = computed(() =>
    isLastDocuResponsible.value
      ? t(
          'digitalizationProjectDocuResponsibles.cannotRemoveLastDocuResponsible',
        )
      : '',
  );

  const { isUserRole } = useRoles();
  function displayAddDocuResponsibleButton(): boolean {
    return isUserRole([Role.Admin, Role.DocuResponsible]);
  }

  const modalRef = ref<InstanceType<typeof SpGroupModal> | null>(null);
  function onAddDocuResponsibleButtonClick(): void {
    modalRef.value?.show();
  }
</script>

<style scoped lang="scss">
  .digitalization-project-docu-responsibles {
    border-top: 1px solid $border-color;
    padding-top: 2rem;
  }
</style>
